import type { GridFilterModel } from "@mui/x-data-grid-premium";
import {
  type InfiniteData,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
  type UseInfiniteQueryResult,
} from "@tanstack/react-query";

import { useDataProvider } from "src/system/useDataProvider.ts";
import { type DefaultError } from "@tanstack/react-query";
import type {
  GridSortModel,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import { useResourceQueries } from "src/framework/useResourceQueries.ts";
import type { ValidGetListResponse } from "src/system/SystemProvider.tsx";
import { logger } from "src/logger";
import { useContentLocale } from "src/auth/hooks/useContentLocale.tsx";
import type { ListQueryKeyParams } from "src/framework/createModuleQueries.ts";

export type UseInfiniteListProps<
  Row extends GridValidRowModel = GridValidRowModel,
> = {
  resource: string;
  option?: string;
  filter?: GridFilterModel;
  groupKeys?: string[];
  include?: string[];
  sort?: GridSortModel;
  pageSize?: number;
  paginationMode?: "simple";
  initialPage?: number;
  locale?: string;
  queryOptions?: Omit<
    UseInfiniteQueryOptions<
      InfiniteData<ValidGetListResponse<Row>>,
      DefaultError,
      InfiniteData<ValidGetListResponse<Row>>,
      any,
      any,
      number
    >,
    "queryKey" | "queryFn" | "getNextPageParam" | "initialPageParam"
  >;
};

type UseInfiniteListReturn<Row extends GridValidRowModel = GridValidRowModel> =
  {
    query: UseInfiniteQueryResult<
      InfiniteData<ValidGetListResponse<Row>>,
      DefaultError
    >;
    queryKey: QueryKey;
    getQueryKey: (params: Partial<ListQueryKeyParams>) => QueryKey;
  };

export const useInfiniteList = <
  Row extends GridValidRowModel = GridValidRowModel,
>(
  props: UseInfiniteListProps<Row>,
): UseInfiniteListReturn<Row> => {
  const {
    resource,
    filter,
    paginationMode = "simple",
    sort,
    locale: localeProp,
    pageSize = 35,
    initialPage = 1,
    groupKeys,
    include,
    option,
    queryOptions,
  } = props;
  const { getList } = useDataProvider();
  const defaultLocale = useContentLocale();
  const queries = useResourceQueries();
  const locale = localeProp || defaultLocale || "de";

  const queryKey = queries.list({
    filter,
    sort,
    include,
    option,
    groupKeys,
    isInfinite: true,
    locale,
    resource,
  });

  const getQueryKey = (params: Partial<ListQueryKeyParams>) => {
    return queries.list({
      filter,
      sort,
      include,
      option,
      groupKeys,
      paginationMode,
      isInfinite: true,
      locale,
      resource,
      ...params,
    });
  };

  const query = useInfiniteQuery({
    queryKey: queryKey,
    queryFn: async ({ signal, pageParam }) => {
      try {
        const params = {
          resource,
          signal,
          filter,
          groupKeys,
          include,
          paginationMode,
          sort,
          page: {
            number: pageParam,
            size: pageSize,
          },
          locale,
          option,
        };
        return (await getList(params)) as ValidGetListResponse<Row>;
      } catch (e: any) {
        logger.error(e);
        throw e;
      }
    },
    initialPageParam: initialPage,
    getNextPageParam: (response, allPages, lastPageParam) => {
      if (paginationMode === "simple") {
        if (response.data.length < pageSize) {
          return undefined;
        }
        return lastPageParam + 1;
      }

      if (lastPageParam + 1 > response.meta.last_page) {
        return undefined;
      }
      return lastPageParam + 1;
    },
    ...queryOptions,
  });
  return {
    query,
    queryKey,
    getQueryKey,
  };
};
